import { useEffect, useRef, useState } from "react";
import { cn } from "@/shared/lib/utils";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";
import {
  CheckIcon,
  Grid3x3Icon,
  InfoIcon,
  Loader2Icon,
  SparklesIcon,
} from "lucide-react";
import type { MatrixListItemUi } from "@/domains/matrix";
import { MatrixActionsMenu } from "./matrix-actions-menu";
import { CreateMatrixDialog } from "./create-matrix-dialog";
import { MatrixQuestionField } from "./matrix-question-field";
import { MatrixInfoDialog } from "./matrix-info-dialog";
import { MatrixTitleMenu } from "./matrix-title-menu";
import { HINT_DURATION_MS, HINT_EXIT_MS } from "../lib/hint-duration";
import { useMatrixRailCollapsed } from "../lib/matrix-rail-state";

type DraftState = { source: string; value: string };

function createDraftState(source: string): DraftState {
  return { source, value: source };
}

type MatrixToolbarProps = {
  name: string;
  question: string;
  collectionId: string;
  matrices: MatrixListItemUi[];
  onCommitName: (name: string) => void;
  onStartAnalysis: () => void;
  isStartingAnalysis: boolean;
  canStartAnalysis: boolean;
  startDisabledReason: string | null;
  isSaving: boolean;
  onRequestDelete: () => void;
  onDuplicate: () => void;
  isDuplicating: boolean;
};

export function MatrixToolbar({
  name,
  question,
  collectionId,
  matrices,
  onCommitName,
  onStartAnalysis,
  isStartingAnalysis,
  canStartAnalysis,
  startDisabledReason,
  isSaving,
  onRequestDelete,
  onDuplicate,
  isDuplicating,
}: MatrixToolbarProps) {
  const [nameDraft, setNameDraft] = useState(() => createDraftState(name));
  const [isEditingName, setIsEditingName] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isInfoOpen, setIsInfoOpen] = useState(false);
  const [isInfoHintOpen, setIsInfoHintOpen] = useState(true);
  const [showsHintLabel, setShowsHintLabel] = useState(true);
  const [isRailCollapsed] = useMatrixRailCollapsed();
  const nameEditorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isInfoHintOpen) return;
    const timer = setTimeout(() => setIsInfoHintOpen(false), HINT_DURATION_MS);
    return () => clearTimeout(timer);
  }, [isInfoHintOpen]);

  // Swapped only after the bubble has faded, so the text never changes mid-animation.
  useEffect(() => {
    if (isInfoHintOpen || !showsHintLabel) return;
    const timer = setTimeout(() => setShowsHintLabel(false), HINT_EXIT_MS);
    return () => clearTimeout(timer);
  }, [isInfoHintOpen, showsHintLabel]);

  // Not while a save is pending: a refetch would clobber a newer edit.
  let currentNameDraft = nameDraft;
  if (currentNameDraft.source !== name && !isSaving) {
    currentNameDraft = createDraftState(name);
    setNameDraft(currentNameDraft);
  }

  function commitName() {
    const trimmed = currentNameDraft.value.trim();
    if (trimmed && trimmed !== name) {
      onCommitName(trimmed);
    } else {
      setNameDraft(createDraftState(name));
    }
    setIsEditingName(false);
  }

  function cancelNameEditing() {
    setNameDraft(createDraftState(name));
    setIsEditingName(false);
  }

  // Pointer based, so clicking something that never takes focus discards too.
  useEffect(() => {
    if (!isEditingName) return;

    function handlePointerDown(event: MouseEvent) {
      const editor = nameEditorRef.current;
      if (!editor || editor.contains(event.target as Node)) return;
      cancelNameEditing();
    }

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  });

  const startAnalysisDisabled = !canStartAnalysis || isStartingAnalysis;

  return (
    <div className="border-primary/20 bg-primary/5 @container/toolbar rounded-xl border p-4 shadow-sm">
      {/* Both dialogs live outside what opens them: a menu unmounts as it
          closes, and the name row is swapped out when editing starts. */}
      <CreateMatrixDialog open={isCreateOpen} onOpenChange={setIsCreateOpen} />
      <MatrixInfoDialog open={isInfoOpen} onOpenChange={setIsInfoOpen} />
      {/* Wrapping only at the narrow end: a wrapping flex line never gets
          around to shrinking anything, so a long title would break the row. */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-3 md:flex-nowrap">
        <div className="bg-primary/10 text-primary flex size-10 shrink-0 items-center justify-center rounded-lg">
          <Grid3x3Icon className="size-5" aria-hidden />
        </div>
        <div
          ref={nameEditorRef}
          className={cn(
            "flex h-8 min-w-0 items-center gap-1",
            isEditingName ? "flex-1 basis-40" : "flex-initial",
          )}
        >
          {isEditingName ? (
            <>
              <Input
                autoFocus
                value={currentNameDraft.value}
                onChange={(e) =>
                  setNameDraft({ ...currentNameDraft, value: e.target.value })
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter") commitName();
                  if (e.key === "Escape") cancelNameEditing();
                }}
                onBlur={(e) => {
                  const next = e.relatedTarget;
                  if (
                    next instanceof Node &&
                    nameEditorRef.current?.contains(next)
                  ) {
                    return;
                  }
                  cancelNameEditing();
                }}
                disabled={isSaving}
                aria-label="Matrix title"
                spellCheck={false}
                className="bg-background dark:bg-background -ml-2 h-8 min-w-0 flex-1 px-2 text-base font-semibold tracking-tight md:text-base"
              />
              <Button
                size="icon-sm"
                onClick={commitName}
                disabled={isSaving}
                aria-label="Save matrix title"
                title="Save (Enter)"
                className="shrink-0"
              >
                {isSaving ? (
                  <Loader2Icon className="size-3.5 animate-spin" />
                ) : (
                  <CheckIcon className="size-3.5" />
                )}
              </Button>
            </>
          ) : (
            <>
              <h1 className="contents">
                <span
                  className={cn("contents", isRailCollapsed ? "" : "lg:hidden")}
                >
                  <MatrixTitleMenu
                    name={name}
                    matrices={matrices}
                    activeCollectionId={collectionId}
                    onCreate={() => setIsCreateOpen(true)}
                  />
                </span>
                {isRailCollapsed ? null : (
                  <span className="hidden min-w-0 truncate text-base leading-8 font-semibold tracking-tight lg:block">
                    {name}
                  </span>
                )}
              </h1>
              <MatrixActionsMenu
                name={name}
                onRename={() => setIsEditingName(true)}
                onDuplicate={onDuplicate}
                onRequestDelete={onRequestDelete}
                isDuplicating={isDuplicating}
                triggerClassName={isRailCollapsed ? "-ml-1.5" : undefined}
              />
            </>
          )}
        </div>

        {/* Gives up width before the title does: the shrink factor is high
            enough that the name only truncates once this is at its floor, which
            is what the pill and the button need side by side. Hidden below that
            in CSS rather than by unmounting, so the field keeps measuring. */}
        {isEditingName ? null : (
          <span className="hidden min-w-[8.5rem] flex-1 shrink-[999] basis-52 @min-[40rem]/toolbar:ml-3 @min-[40rem]/toolbar:block">
            <MatrixQuestionField question={question} />
          </span>
        )}

        <div className="ml-auto flex shrink-0 items-center gap-2">
          {/* Forced open once per visit, then an ordinary hover tooltip. The
              remount is required: a tooltip held open from the outside will not
              open on hover again by itself. */}
          <Tooltip
            key={isInfoHintOpen ? "hint" : "hover"}
            open={isInfoHintOpen ? true : undefined}
          >
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => {
                  setIsInfoHintOpen(false);
                  setIsInfoOpen(true);
                }}
                className="text-muted-foreground hover:text-primary size-6 shrink-0"
                aria-label="How matrices work"
              >
                <InfoIcon className="size-3.5" aria-hidden />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top">
              {showsHintLabel
                ? "New to matrices? This explains how they work."
                : "How matrices work"}
            </TooltipContent>
          </Tooltip>
          {startDisabledReason ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button className="gap-1.5" size="sm" disabled>
                    <SparklesIcon className="size-3.5" />
                    Start Analysis
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent side="top">{startDisabledReason}</TooltipContent>
            </Tooltip>
          ) : (
            <Button
              className="gap-1.5"
              size="sm"
              onClick={onStartAnalysis}
              disabled={startAnalysisDisabled}
            >
              {isStartingAnalysis ? (
                <Loader2Icon className="size-3.5 animate-spin" />
              ) : (
                <SparklesIcon className="size-3.5" />
              )}
              Start Analysis
            </Button>
          )}
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <Button variant="outline" size="sm" disabled>
                  Share
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent side="top">Sharing is coming soon</TooltipContent>
          </Tooltip>
        </div>
      </div>
    </div>
  );
}
